/**
 * SQLite-backed event queue for critical error monitoring.
 *
 * Uses @capacitor-community/sqlite (jeep-sqlite / sql.js in-memory on web).
 * Falls back to a process-local memory store if the plugin is unavailable.
 *
 * Tables:
 *  - pending_events: queued events waiting to be POSTed
 *  - push_log: successful pushes used for 3-per-4h rate limiting by error type
 */

import { Capacitor } from '@capacitor/core';
import { CapacitorSQLite, SQLiteConnection } from '@capacitor-community/sqlite';

const DB_NAME = 'monitoring_events';
const DB_VERSION = 1;

export const BATCH_SIZE = 10;
export const FLUSH_INTERVAL_MS = 60 * 60 * 1000; // 1 hour
export const MAX_PUSHES_PER_TYPE = 3;
export const RATE_LIMIT_WINDOW_MS = 4 * 60 * 60 * 1000; // 4 hours

/** error type = category + endpoint (or title) — caps pushes of the same kind */
export function getErrorType(event) {
    const category = event?.category || 'other';
    const key = event?.endpoint || event?.title || event?.message || '';
    return `${category}|${String(key).slice(0, 200)}`;
}

const memory = {
    pending: [],
    pushLog: [],
};

let sqlite = null;
let db = null;
let initPromise = null;
let useMemory = false;

async function initWebStore(connection) {
    if (Capacitor.getPlatform() !== 'web') return;

    const { defineCustomElements } = await import('jeep-sqlite/loader');
    await defineCustomElements(window);

    if (!document.querySelector('jeep-sqlite')) {
        const el = document.createElement('jeep-sqlite');
        document.body.appendChild(el);
    }
    await customElements.whenDefined('jeep-sqlite');
    await connection.initWebStore();
}

async function openSqliteDb() {
    sqlite = new SQLiteConnection(CapacitorSQLite);
    await initWebStore(sqlite);

    const consistency = await sqlite.checkConnectionsConsistency();
    const isConn = (await sqlite.isConnection(DB_NAME, false)).result;

    if (consistency.result && isConn) {
        db = await sqlite.retrieveConnection(DB_NAME, false);
    } else {
        db = await sqlite.createConnection(DB_NAME, false, 'no-encryption', DB_VERSION, false);
    }

    await db.open();
    await db.execute(`
        CREATE TABLE IF NOT EXISTS pending_events (
            id TEXT PRIMARY KEY NOT NULL,
            error_type TEXT NOT NULL,
            payload TEXT NOT NULL,
            created_at INTEGER NOT NULL
        );
        CREATE TABLE IF NOT EXISTS push_log (
            id INTEGER PRIMARY KEY AUTOINCREMENT NOT NULL,
            error_type TEXT NOT NULL,
            pushed_at INTEGER NOT NULL
        );
        CREATE INDEX IF NOT EXISTS idx_pending_created ON pending_events(created_at);
        CREATE INDEX IF NOT EXISTS idx_push_log_type_time ON push_log(error_type, pushed_at);
    `);
}

/**
 * Ensure the store is ready. Safe to call repeatedly.
 * Switches to memory fallback if SQLite init fails (e.g. plugin not synced yet).
 */
export async function ensureEventStore() {
    if (db || useMemory) return;
    if (initPromise) return initPromise;

    initPromise = (async () => {
        try {
            await openSqliteDb();
            useMemory = false;
        } catch (e) {
            console.warn('[eventStore] SQLite unavailable, using memory fallback:', e?.message);
            useMemory = true;
            db = null;
            sqlite = null;
        }
    })();

    try {
        await initPromise;
    } finally {
        initPromise = null;
    }
}

function readCount(queryResult) {
    const row = queryResult?.values?.[0];
    if (row == null) return 0;
    if (typeof row === 'number') return row;
    if (Array.isArray(row)) return Number(row[0] ?? 0);
    return Number(row.cnt ?? row.CNT ?? row['COUNT(*)'] ?? Object.values(row)[0] ?? 0);
}

function pruneMemoryPushLog(now = Date.now()) {
    const cutoff = now - RATE_LIMIT_WINDOW_MS;
    memory.pushLog = memory.pushLog.filter((row) => row.pushedAt >= cutoff);
}

async function prunePushLog(now = Date.now()) {
    const cutoff = now - RATE_LIMIT_WINDOW_MS;
    if (useMemory || !db) {
        pruneMemoryPushLog(now);
        return;
    }
    await db.run('DELETE FROM push_log WHERE pushed_at < ?', [cutoff]);
}

async function countPushed(errorType, now = Date.now()) {
    const cutoff = now - RATE_LIMIT_WINDOW_MS;
    if (useMemory || !db) {
        pruneMemoryPushLog(now);
        return memory.pushLog.filter(
            (row) => row.errorType === errorType && row.pushedAt >= cutoff
        ).length;
    }
    const res = await db.query(
        'SELECT COUNT(*) AS cnt FROM push_log WHERE error_type = ? AND pushed_at >= ?',
        [errorType, cutoff]
    );
    return readCount(res);
}

async function countPendingByType(errorType) {
    if (useMemory || !db) {
        return memory.pending.filter((row) => row.errorType === errorType).length;
    }
    const res = await db.query(
        'SELECT COUNT(*) AS cnt FROM pending_events WHERE error_type = ?',
        [errorType]
    );
    return readCount(res);
}

/** How many more events of this type may still be pushed in the current 4h window. */
export async function getRemainingPushSlots(errorType) {
    await ensureEventStore();
    const now = Date.now();
    await prunePushLog(now);
    const pushed = await countPushed(errorType, now);
    return Math.max(0, MAX_PUSHES_PER_TYPE - pushed);
}

/** True if enqueueing another event of this type would exceed the 3 / 4h push cap. */
export async function wouldExceedRateLimit(errorType) {
    await ensureEventStore();
    const now = Date.now();
    await prunePushLog(now);
    const pushed = await countPushed(errorType, now);
    const pending = await countPendingByType(errorType);
    return pushed + pending >= MAX_PUSHES_PER_TYPE;
}

export async function enqueueEvent(event) {
    await ensureEventStore();
    const errorType = getErrorType(event);
    const createdAt = Date.now();
    const payload = JSON.stringify(event);

    if (useMemory || !db) {
        memory.pending.push({
            id: event.id,
            errorType,
            payload: event,
            createdAt,
        });
        return memory.pending.length;
    }

    await db.run(
        'INSERT OR IGNORE INTO pending_events (id, error_type, payload, created_at) VALUES (?, ?, ?, ?)',
        [event.id, errorType, payload, createdAt]
    );

    // Keep web DB in-memory for the session; do not saveToStore.
    const res = await db.query('SELECT COUNT(*) AS cnt FROM pending_events', []);
    return readCount(res);
}

export async function getPendingCount() {
    await ensureEventStore();
    if (useMemory || !db) return memory.pending.length;

    const res = await db.query('SELECT COUNT(*) AS cnt FROM pending_events', []);
    return readCount(res);
}

/** Take up to `limit` oldest pending events (does not delete). */
export async function peekPendingEvents(limit = BATCH_SIZE) {
    await ensureEventStore();

    if (useMemory || !db) {
        return memory.pending
            .slice()
            .sort((a, b) => a.createdAt - b.createdAt)
            .slice(0, limit)
            .map((row) => ({
                id: row.id,
                errorType: row.errorType,
                event: row.payload,
            }));
    }

    const res = await db.query(
        'SELECT id, error_type, payload FROM pending_events ORDER BY created_at ASC LIMIT ?',
        [limit]
    );
    const rows = res?.values || [];
    return rows.map((row) => ({
        id: row.id,
        errorType: row.error_type ?? row.errorType,
        event: typeof row.payload === 'string' ? JSON.parse(row.payload) : row.payload,
    }));
}

/** Remove successfully pushed events and record them in push_log. */
export async function markEventsPushed(rows) {
    if (!rows?.length) return;
    await ensureEventStore();
    const now = Date.now();

    if (useMemory || !db) {
        const ids = new Set(rows.map((r) => r.id));
        memory.pending = memory.pending.filter((row) => !ids.has(row.id));
        for (const row of rows) {
            memory.pushLog.push({ errorType: row.errorType, pushedAt: now });
        }
        pruneMemoryPushLog(now);
        return;
    }

    const placeholders = rows.map(() => '?').join(',');
    const ids = rows.map((r) => r.id);
    await db.run(
        `DELETE FROM pending_events WHERE id IN (${placeholders})`,
        ids
    );

    for (const row of rows) {
        await db.run(
            'INSERT INTO push_log (error_type, pushed_at) VALUES (?, ?)',
            [row.errorType, now]
        );
    }
    await prunePushLog(now);
}

/** Drop pending rows that already hit the rate limit (e.g. after a prior flush). */
export async function dropRateLimitedPending(rows) {
    if (!rows?.length) return;
    await ensureEventStore();

    if (useMemory || !db) {
        const ids = new Set(rows.map((r) => r.id));
        memory.pending = memory.pending.filter((row) => !ids.has(row.id));
        return;
    }

    const placeholders = rows.map(() => '?').join(',');
    await db.run(
        `DELETE FROM pending_events WHERE id IN (${placeholders})`,
        rows.map((r) => r.id)
    );
}

/** @returns {'sqlite'|'memory'|null} */
export function getEventStoreBackend() {
    if (db) return 'sqlite';
    if (useMemory) return 'memory';
    return null;
}
